import { Icon, Link } from '@hotjar/hotsauce';

import type { ResponseLimitInfo } from '@hotjar/feedback/domain/common/Responses/ResponseLimitInfo';

import { Trans, useTranslationWithPrefix } from '@hotjar/translations';

interface CardWidgetProps {
  responseLimitInfo: ResponseLimitInfo | undefined;
  lastActiveDaysAgo: number;
  onVerificationClick: () => void;
  isError: boolean;
}

const ButtonLink = ({ children, onClick }: { children?: string; onClick?: () => void }) => {
  return (
    <Link kind="inline" as="button" onClick={onClick}>
      {children as string}
    </Link>
  );
};

const InfoIcon = () => {
  return (
    <div data-testid="data-limits-ask-widget-info-icon">
      <Icon inline color="text_light" icon="info" size="m" />
    </div>
  );
};

const WarningIcon = () => {
  return (
    <div data-testid="data-limits-ask-widget-warning-icon">
      <Icon inline color="text_light" icon="warning" size="m" />
    </div>
  );
};

const SuccessIcon = () => {
  return (
    <div data-testid="data-limits-ask-widget-success-icon">
      <Icon inline color="text_light" icon="check" size="m" />
    </div>
  );
};

export const CardWidget = ({
  responseLimitInfo,
  lastActiveDaysAgo,
  onVerificationClick,
  isError,
}: CardWidgetProps): JSX.Element => {
  const { t } = useTranslationWithPrefix('billing:product-select-upsell-modal.ask-limits-card');

  if (isError) {
    return (
      <>
        <InfoIcon />
        {t('responses-remaining.error')}
      </>
    );
  }

  if (lastActiveDaysAgo > 0) {
    return (
      <>
        <WarningIcon />
        <div>
          <Trans
            i18nKey="billing:product-select-upsell-modal.ask-limits-card.responses-remaining.no-data"
            components={[<ButtonLink onClick={onVerificationClick} />]}
            values={{
              daysWithoutData: lastActiveDaysAgo,
            }}
          />
        </div>
      </>
    );
  }

  const noneRemaining = responseLimitInfo?.unlimited === false && responseLimitInfo.remaining === 0;
  if (noneRemaining) {
    return (
      <>
        <WarningIcon />
        {t('responses-remaining.none')}
      </>
    );
  }

  const responsesRemaining =
    responseLimitInfo?.unlimited === false
      ? responseLimitInfo.remaining
      : t('responses-remaining.unlimited');

  const lowData =
    responseLimitInfo?.unlimited === false &&
    responseLimitInfo.remaining / responseLimitInfo.limit < 0.5;
  if (lowData) {
    return (
      <>
        <WarningIcon />
        <Trans
          i18nKey="billing:product-select-upsell-modal.ask-limits-card.responses-remaining.low"
          values={{
            responsesRemaining,
          }}
        />
      </>
    );
  }

  return (
    <>
      <SuccessIcon />
      <Trans
        i18nKey="billing:product-select-upsell-modal.ask-limits-card.responses-remaining.default"
        values={{
          responsesRemaining,
        }}
      />
    </>
  );
};
